import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';

interface CalculatorState {
  plan: 2950 | 4950 | 8950;
  closeRate: number;
  buyerCommission: number;
  tenantCommission: number;
  fsboConversionRate: number;
}

// Plan definitions
const plans = [
  {
    id: 'essential',
    label: 'Essential',
    price: 2950
  },
  {
    id: 'premium',
    label: 'Premium',
    price: 4950
  },
  {
    id: 'elite',
    label: 'Elite',
    price: 8950
  }
] as const;

// Helper function to format numbers with 'k' notation
const formatCurrency = (value: number): string => {
  if (value >= 1000) {
    const kValue = value / 1000;
    // Always round to whole number, no decimals
    return `$${Math.round(kValue)}k`;
  }
  return `$${value.toLocaleString()}`;
};

// Helper function to format number ranges with 'k' notation
const formatCurrencyRange = (min: number, max: number): string => {
  return `${formatCurrency(min)}-${formatCurrency(max)}`;
};

export const ROIPricingCalculator = () => {
  const [state, setState] = useState<CalculatorState>({
    plan: 4950,
    closeRate: 9,
    buyerCommission: 13000,
    tenantCommission: 1150,
    fsboConversionRate: 7
  });

  const [isRevenueBreakdownOpen, setIsRevenueBreakdownOpen] = useState(false);
  const [isLeadsBreakdownOpen, setIsLeadsBreakdownOpen] = useState(false);
  const [isKeyFactsOpen, setIsKeyFactsOpen] = useState(true);

  const updateState = <K extends keyof CalculatorState>(
    key: K,
    value: CalculatorState[K]
  ) => {
    setState(prev => ({ ...prev, [key]: value }));
  };

  // Get current plan
  const currentPlan = useMemo(() => {
    return plans.find(plan => plan.price === state.plan) || plans[1];
  }, [state.plan]);

  // Calculate FSBO listings range based on plan tier
  const fsboListingsRange = useMemo(() => {
    if (currentPlan.id === 'essential') return { min: 3, max: 5 };
    if (currentPlan.id === 'premium') return { min: 8, max: 10 };
    return { min: 15, max: 20 }; // Elite
  }, [currentPlan.id]);

  // Lead ranges (5-6 leads per listing)
  const totalLeadsRange = useMemo(
    () => ({
      min: Math.floor(fsboListingsRange.min * 5),
      max: Math.floor(fsboListingsRange.max * 6)
    }),
    [fsboListingsRange]
  );

  // Calculate min and max revenue
  const minRevenue = useMemo(() => {
    const minListings = fsboListingsRange.min;
    const minLeads = minListings * 5;
    const minBuyerLeads = minLeads * 0.3;
    const minLeaseLeads = minLeads * 0.7;
    const minBuyerConversions = (minBuyerLeads * state.closeRate) / 100;
    const minLeaseConversions = (minLeaseLeads * state.closeRate) / 100;
    const minFsboFullService = (minListings * state.fsboConversionRate) / 100;
    const minBuyerRevenue = minBuyerConversions * state.buyerCommission;
    const minLeaseRevenue = minLeaseConversions * state.tenantCommission;
    const minFsboRevenue = minFsboFullService * state.buyerCommission;
    const minMonthlyRevenue =
      minBuyerRevenue + minLeaseRevenue + minFsboRevenue;
    return Math.floor(minMonthlyRevenue * 12);
  }, [
    fsboListingsRange,
    state.closeRate,
    state.buyerCommission,
    state.tenantCommission,
    state.fsboConversionRate
  ]);

  const maxRevenue = useMemo(() => {
    const maxListings = fsboListingsRange.max;
    const maxLeads = maxListings * 6;
    const maxBuyerLeads = maxLeads * 0.3;
    const maxLeaseLeads = maxLeads * 0.7;
    const maxBuyerConversions = (maxBuyerLeads * state.closeRate) / 100;
    const maxLeaseConversions = (maxLeaseLeads * state.closeRate) / 100;
    const maxFsboFullService = (maxListings * state.fsboConversionRate) / 100;
    const maxBuyerRevenue = maxBuyerConversions * state.buyerCommission;
    const maxLeaseRevenue = maxLeaseConversions * state.tenantCommission;
    const maxFsboRevenue = maxFsboFullService * state.buyerCommission;
    const maxMonthlyRevenue =
      maxBuyerRevenue + maxLeaseRevenue + maxFsboRevenue;
    return Math.floor(maxMonthlyRevenue * 12);
  }, [
    fsboListingsRange,
    state.closeRate,
    state.buyerCommission,
    state.tenantCommission,
    state.fsboConversionRate
  ]);

  // Calculate annual plan cost
  const annualPlanCost = useMemo(() => state.plan * 12, [state.plan]);

  // Calculate ROI multiplier range (revenue / annual cost)
  const roiRange = useMemo(
    () => ({
      min: minRevenue / annualPlanCost,
      max: maxRevenue / annualPlanCost
    }),
    [minRevenue, maxRevenue, annualPlanCost]
  );

  // Calculate average ROI for progress bar
  const avgROI = useMemo(() => {
    return (roiRange.min + roiRange.max) / 2;
  }, [roiRange]);

  // Calculate ROI percentage for progress bar (using 7x as max for visual scale)
  const roiPercentage = useMemo(() => {
    const maxROIForDisplay = 7; // 7x ROI = 100% of bar
    return Math.min(100, (avgROI / maxROIForDisplay) * 100);
  }, [avgROI]);

  // Calculate revenue breakdowns
  const buyerRevenueBreakdown = useMemo(() => {
    const minListings = fsboListingsRange.min;
    const maxListings = fsboListingsRange.max;
    const minLeads = minListings * 5;
    const maxLeads = maxListings * 6;
    const minBuyerLeads = minLeads * 0.3;
    const maxBuyerLeads = maxLeads * 0.3;
    const minBuyerConversions = (minBuyerLeads * state.closeRate) / 100;
    const maxBuyerConversions = (maxBuyerLeads * state.closeRate) / 100;
    const minBuyerRevenue = minBuyerConversions * state.buyerCommission * 12;
    const maxBuyerRevenue = maxBuyerConversions * state.buyerCommission * 12;
    return {
      min: Math.floor(minBuyerRevenue),
      max: Math.floor(maxBuyerRevenue),
      minClosings: Math.floor(minBuyerConversions * 12),
      maxClosings: Math.floor(maxBuyerConversions * 12)
    };
  }, [fsboListingsRange, state.closeRate, state.buyerCommission]);

  const tenantRevenueBreakdown = useMemo(() => {
    const minListings = fsboListingsRange.min;
    const maxListings = fsboListingsRange.max;
    const minLeads = minListings * 5;
    const maxLeads = maxListings * 6;
    const minLeaseLeads = minLeads * 0.7;
    const maxLeaseLeads = maxLeads * 0.7;
    const minLeaseConversions = (minLeaseLeads * state.closeRate) / 100;
    const maxLeaseConversions = (maxLeaseLeads * state.closeRate) / 100;
    const minLeaseRevenue = minLeaseConversions * state.tenantCommission * 12;
    const maxLeaseRevenue = maxLeaseConversions * state.tenantCommission * 12;
    return {
      min: Math.floor(minLeaseRevenue),
      max: Math.floor(maxLeaseRevenue),
      minClosings: Math.floor(minLeaseConversions * 12),
      maxClosings: Math.floor(maxLeaseConversions * 12)
    };
  }, [fsboListingsRange, state.closeRate, state.tenantCommission]);

  const fsboRevenueBreakdown = useMemo(() => {
    const minListings = fsboListingsRange.min;
    const maxListings = fsboListingsRange.max;
    const minFsboFullService = (minListings * state.fsboConversionRate) / 100;
    const maxFsboFullService = (maxListings * state.fsboConversionRate) / 100;
    const minFsboRevenue = minFsboFullService * state.buyerCommission * 12;
    const maxFsboRevenue = maxFsboFullService * state.buyerCommission * 12;
    return {
      min: Math.floor(minFsboRevenue),
      max: Math.floor(maxFsboRevenue),
      minConversions: Math.floor(minFsboFullService * 12),
      maxConversions: Math.floor(maxFsboFullService * 12)
    };
  }, [fsboListingsRange, state.fsboConversionRate, state.buyerCommission]);

  // Calculate leads breakdown
  const leadsBreakdown = useMemo(() => {
    const minTotalLeads = totalLeadsRange.min * 12;
    const maxTotalLeads = totalLeadsRange.max * 12;

    const buyerLeads = {
      min: Math.floor(minTotalLeads * 0.3),
      max: Math.floor(maxTotalLeads * 0.3)
    };

    const leaseLeads = {
      min: Math.floor(minTotalLeads * 0.7),
      max: Math.floor(maxTotalLeads * 0.7)
    };

    const fsboLeads =
      currentPlan.id !== 'essential'
        ? {
            min: fsboListingsRange.min * 12,
            max: fsboListingsRange.max * 12
          }
        : null;

    return {
      buyer: buyerLeads,
      lease: leaseLeads,
      fsbo: fsboLeads
    };
  }, [totalLeadsRange, fsboListingsRange, currentPlan.id]);

  // Calculate pie chart data for leads breakdown
  const leadsPieChartData = useMemo(() => {
    const buyerAvg = (leadsBreakdown.buyer.min + leadsBreakdown.buyer.max) / 2;
    const leaseAvg = (leadsBreakdown.lease.min + leadsBreakdown.lease.max) / 2;
    const fsboAvg = leadsBreakdown.fsbo
      ? (leadsBreakdown.fsbo.min + leadsBreakdown.fsbo.max) / 2
      : 0;

    const total = buyerAvg + leaseAvg + fsboAvg;

    const data = [
      {
        label: 'Buyer Leads',
        chartLabel: 'Buyers',
        value: buyerAvg,
        percentage: total > 0 ? (buyerAvg / total) * 100 : 0,
        color: '#b8a8e8',
        min: leadsBreakdown.buyer.min,
        max: leadsBreakdown.buyer.max
      },
      {
        label: 'Lease Leads',
        chartLabel: 'Leases',
        value: leaseAvg,
        percentage: total > 0 ? (leaseAvg / total) * 100 : 0,
        color: '#c9bdf0',
        min: leadsBreakdown.lease.min,
        max: leadsBreakdown.lease.max
      }
    ];

    if (leadsBreakdown.fsbo) {
      data.push({
        label: 'FSBO Leads',
        chartLabel: 'FSBO',
        value: fsboAvg,
        percentage: total > 0 ? (fsboAvg / total) * 100 : 0,
        color: '#d8d0f5',
        min: leadsBreakdown.fsbo.min,
        max: leadsBreakdown.fsbo.max
      });
    }

    // Calculate angles for pie chart
    let currentAngle = -90; // Start at top
    const segments = data.map(item => {
      const angle = (item.percentage / 100) * 360;
      const startAngle = currentAngle;
      const endAngle = currentAngle + angle;
      const midAngle = (startAngle + endAngle) / 2;
      currentAngle = endAngle;

      // Calculate path for pie slice
      const radius = 80;
      const centerX = 100;
      const centerY = 100;

      const startAngleRad = (startAngle * Math.PI) / 180;
      const endAngleRad = (endAngle * Math.PI) / 180;
      const midAngleRad = (midAngle * Math.PI) / 180;

      const x1 = centerX + radius * Math.cos(startAngleRad);
      const y1 = centerY + radius * Math.sin(startAngleRad);
      const x2 = centerX + radius * Math.cos(endAngleRad);
      const y2 = centerY + radius * Math.sin(endAngleRad);

      // Calculate label position (at 60% of radius)
      const labelRadius = radius * 0.6;
      const labelX = centerX + labelRadius * Math.cos(midAngleRad);
      const labelY = centerY + labelRadius * Math.sin(midAngleRad);

      const largeArcFlag = angle > 180 ? 1 : 0;

      const path = `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;

      return {
        ...item,
        path,
        labelX,
        labelY
      };
    });

    return segments;
  }, [leadsBreakdown]);

  // Calculate pie chart data for revenue breakdown
  const pieChartData = useMemo(() => {
    const buyerAvg =
      (buyerRevenueBreakdown.min + buyerRevenueBreakdown.max) / 2;
    const tenantAvg =
      (tenantRevenueBreakdown.min + tenantRevenueBreakdown.max) / 2;
    const fsboAvg =
      currentPlan.id !== 'essential'
        ? (fsboRevenueBreakdown.min + fsboRevenueBreakdown.max) / 2
        : 0;

    const total = buyerAvg + tenantAvg + fsboAvg;

    const data = [
      {
        label: 'Buyer Commissions',
        value: buyerAvg,
        percentage: total > 0 ? (buyerAvg / total) * 100 : 0,
        color: '#b8a8e8', // Subtle purple
        min: buyerRevenueBreakdown.min,
        max: buyerRevenueBreakdown.max,
        closings: `${buyerRevenueBreakdown.minClosings} to ${buyerRevenueBreakdown.maxClosings}`
      },
      {
        label: 'Lease Commissions',
        value: tenantAvg,
        percentage: total > 0 ? (tenantAvg / total) * 100 : 0,
        color: '#c9bdf0', // Lighter subtle purple
        min: tenantRevenueBreakdown.min,
        max: tenantRevenueBreakdown.max,
        closings: `${tenantRevenueBreakdown.minClosings} to ${tenantRevenueBreakdown.maxClosings}`
      }
    ];

    if (currentPlan.id !== 'essential') {
      data.push({
        label: 'Mere Posting to Full Service',
        value: fsboAvg,
        percentage: total > 0 ? (fsboAvg / total) * 100 : 0,
        color: '#d8d0f5', // Lightest subtle purple
        min: fsboRevenueBreakdown.min,
        max: fsboRevenueBreakdown.max,
        closings: `${fsboRevenueBreakdown.minConversions} to ${fsboRevenueBreakdown.maxConversions}`
      });
    }

    // Calculate angles for pie chart
    let currentAngle = -90; // Start at top
    const segments = data.map(item => {
      const angle = (item.percentage / 100) * 360;
      const startAngle = currentAngle;
      const endAngle = currentAngle + angle;
      const midAngle = (startAngle + endAngle) / 2; // Middle angle for label positioning
      currentAngle = endAngle;

      // Calculate path for pie slice
      const radius = 80;
      const centerX = 100;
      const centerY = 100;

      const startAngleRad = (startAngle * Math.PI) / 180;
      const endAngleRad = (endAngle * Math.PI) / 180;
      const midAngleRad = (midAngle * Math.PI) / 180;

      const x1 = centerX + radius * Math.cos(startAngleRad);
      const y1 = centerY + radius * Math.sin(startAngleRad);
      const x2 = centerX + radius * Math.cos(endAngleRad);
      const y2 = centerY + radius * Math.sin(endAngleRad);

      // Calculate label position (at 60% of radius for better visibility)
      const labelRadius = radius * 0.6;
      const labelX = centerX + labelRadius * Math.cos(midAngleRad);
      const labelY = centerY + labelRadius * Math.sin(midAngleRad);

      const largeArcFlag = angle > 180 ? 1 : 0;

      const path = `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;

      // Map labels
      const chartLabel =
        item.label === 'Buyer Commissions'
          ? 'Buyers'
          : item.label === 'Lease Commissions'
            ? 'Leases'
            : 'Listings';

      return {
        ...item,
        path,
        startAngle,
        endAngle,
        midAngle,
        labelX,
        labelY,
        chartLabel
      };
    });

    return segments;
  }, [
    buyerRevenueBreakdown,
    tenantRevenueBreakdown,
    fsboRevenueBreakdown,
    currentPlan.id
  ]);

  // Handle plan selection
  const handlePlanSelect = (planId: string) => {
    const selectedPlan = plans.find(p => p.id === planId);
    if (selectedPlan) {
      setState(prev => ({
        ...prev,
        plan: selectedPlan.price as 2950 | 4950 | 8950
      }));
    }
  };

  return (
    <section
      className="bg--fixed hero-section z-[3] relative py-16 md:py-20 lg:py-24 bg-scroll w-full bg-no-repeat bg-[center_center] bg-cover"
      style={{ backgroundImage: 'url("/images/hero-10.jpg")' }}
    >
      <div className="container mx-auto px-4 mt-12 sm:max-md:mt-6 xsm:max-sm:mt-4">
        <div className="max-w-7xl mx-auto" style={{ overflow: 'visible' }}>
          {/* Title */}
          <div className="mb-[30px] md:max-lg:mb-[25px] sm:max-md:mb-[20px] xsm:max-sm:mb-[15px]">
            <div className="wow fadeInRight">
              <h2 className="s-36 w--700 xl:!text-[2.25rem] lg:max-xl:!text-[2rem] md:max-lg:!text-[1.875rem] sm:max-md:!text-[1.75rem] xsm:max-sm:!text-[1.5rem] !font-bold leading-[1.3] !mb-[15px] lg:max-xl:!mb-[12px] md:max-lg:!mb-[10px] sm:max-md:!mb-[10px] xsm:max-sm:!mb-[8px] font-Jakarta !text-[#353f4f]">
                Estimate commissions from purchased leads
              </h2>
              <p className="p-md color--grey !text-[#757575] xl:!mb-[25px] md:max-lg:!text-[1.1rem] md:max-lg:!mb-[15px] sm:max-md:!text-[1.175rem] sm:max-md:!mb-[25px] sm:max-md:!p-[0_3%] xsm:max-sm:!mb-[16px] xsm:max-sm:!p-[0_5%]">
                Model close rates and GCI from REALTOR.ca showing-request volume
                — then compare to plan cost. Actual results depend on follow-up
                speed and your market.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1.8fr_1fr] gap-8 lg:gap-10 lg:items-start">
            {/* Left Card - Input Form */}
            <div className="bg-white rounded-2xl shadow-lg p-8 lg:p-10 border-t-4 border-[#663ae9]">
              {/* Plan Selection */}
              <div className="mb-8">
                <label className="block text-[#353f4f] font-semibold text-base mb-4 font-Jakarta">
                  Choose a plan
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {plans.map(plan => {
                    const isSelected = state.plan === plan.price;
                    return (
                      <button
                        key={plan.id}
                        type="button"
                        onClick={() => handlePlanSelect(plan.id)}
                        className={`group relative overflow-hidden rounded-[10px] border-2 transition-all duration-[400ms] ease-[ease-in-out] cursor-pointer text-left p-4 ${
                          isSelected
                            ? 'bg-gradient-to-br from-[#663ae9] to-[#5a2fd9] border-[#663ae9] shadow-[0px_8px_24px_0px_rgba(102,58,233,0.25)] scale-[1.02]'
                            : 'bg-white border-[#e0e0e0] hover:border-[#663ae9] hover:shadow-[0px_4px_12px_0px_rgba(0,0,0,0.08)]'
                        }`}
                      >
                        {/* Selected indicator */}
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                            <svg
                              className="w-4 h-4 text-white"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={3}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          </div>
                        )}
                        {/* Plan Label */}
                        <div
                          className={`text-[0.8125rem] font-semibold uppercase tracking-wider mb-2 font-Jakarta ${
                            isSelected ? 'text-white/90' : 'text-[#666]'
                          }`}
                        >
                          {plan.label}
                        </div>

                        {/* First month discount badge for Essential plan */}
                        {plan.id === 'essential' && (
                          <div className="mb-2">
                            <span
                              className={`text-[0.75rem] font-semibold font-Jakarta px-2 py-1 ${
                                isSelected
                                  ? 'bg-white text-[#663ae9]'
                                  : 'bg-[#663ae9] text-white'
                              }`}
                            >
                              ✨ 50% Off
                            </span>
                          </div>
                        )}

                        {/* Price */}
                        {plan.id === 'essential' ? (
                          <div>
                            <div className="flex items-baseline gap-1">
                              <span
                                className={`text-[1.75rem] font-bold leading-none font-Jakarta ${
                                  isSelected ? 'text-white' : 'text-[#353f4f]'
                                }`}
                              >
                                $1,475
                              </span>
                              <span
                                className={`text-[0.875rem] font-medium font-Jakarta ${
                                  isSelected ? 'text-white/80' : 'text-[#666]'
                                }`}
                              >
                                /mo
                              </span>
                            </div>
                            <div className="mt-1">
                              <span
                                className={`text-[0.75rem] font-medium font-Jakarta ${
                                  isSelected ? 'text-white/80' : 'text-[#666]'
                                }`}
                              >
                                renews at $2,950/mo
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div className="flex items-baseline gap-1">
                              <span
                                className={`text-[1.75rem] font-bold leading-none font-Jakarta ${
                                  isSelected ? 'text-white' : 'text-[#353f4f]'
                                }`}
                              >
                                ${plan.price.toLocaleString()}
                              </span>
                              <span
                                className={`text-[0.875rem] font-medium font-Jakarta ${
                                  isSelected ? 'text-white/80' : 'text-[#666]'
                                }`}
                              >
                                /mo
                              </span>
                            </div>
                            <div className="mt-1">
                              <span
                                className={`text-[0.75rem] font-medium font-Jakarta ${
                                  isSelected ? 'text-white/80' : 'text-[#666]'
                                }`}
                              >
                                Cancel anytime.
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Hover effect for non-selected */}
                        {!isSelected && (
                          <div className="mt-2 text-[0.75rem] text-[#999] font-medium font-Jakarta opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            Click to select →
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Calculations - Directly Visible */}
              <div className="mb-8 space-y-6">
                {/* Close Rate Slider */}
                <div>
                  <label className="block text-[#353f4f] font-semibold text-base mb-3 font-Jakarta">
                    Close Rate per Lead:{' '}
                    <span className="text-[#663ae9]">{state.closeRate}%</span>
                  </label>
                  <p className="text-sm text-[#666] mb-3 leading-relaxed font-Jakarta">
                    The percentage of showing requests that successfully convert
                    into closed deals. Industry average ranges from 7% to 15%.
                  </p>
                  <input
                    type="range"
                    min="7"
                    max="15"
                    step="0.5"
                    value={state.closeRate}
                    onChange={e =>
                      updateState('closeRate', parseFloat(e.target.value))
                    }
                    className="w-full h-2 bg-[#e0e0e0] rounded-lg appearance-none cursor-pointer roi-slider"
                    style={{
                      background: (() => {
                        const percentage = ((state.closeRate - 7) / 8) * 100;
                        const opacity = 0.3 + (percentage / 100) * 0.7; // Range from 0.3 to 1.0
                        return `linear-gradient(to right, rgba(102, 58, 233, ${opacity}) 0%, rgba(102, 58, 233, ${opacity}) ${percentage}%, #e0e0e0 ${percentage}%, #e0e0e0 100%)`;
                      })()
                    }}
                  />
                  <div className="flex justify-between text-xs text-[#666] mt-2 font-medium">
                    <span>7%</span>
                    <span>15%</span>
                  </div>
                </div>

                {/* Buyer Commission Slider */}
                <div>
                  <label className="block text-[#353f4f] font-semibold text-base mb-3 font-Jakarta">
                    Average Commission - Buyers:{' '}
                    <span className="text-[#663ae9]">
                      ${state.buyerCommission.toLocaleString()}
                    </span>
                  </label>
                  <p className="text-sm text-[#666] mb-3 leading-relaxed font-Jakarta">
                    Average commission earned per successful buyer transaction.
                    Typical range is $5,000 to $20,000.
                  </p>
                  <input
                    type="range"
                    min="5000"
                    max="20000"
                    step="500"
                    value={state.buyerCommission}
                    onChange={e =>
                      updateState('buyerCommission', parseInt(e.target.value))
                    }
                    className="w-full h-2 bg-[#e0e0e0] rounded-lg appearance-none cursor-pointer roi-slider"
                    style={{
                      background: (() => {
                        const percentage =
                          ((state.buyerCommission - 5000) / 15000) * 100;
                        const opacity = 0.3 + (percentage / 100) * 0.7; // Range from 0.3 to 1.0
                        return `linear-gradient(to right, rgba(102, 58, 233, ${opacity}) 0%, rgba(102, 58, 233, ${opacity}) ${percentage}%, #e0e0e0 ${percentage}%, #e0e0e0 100%)`;
                      })()
                    }}
                  />
                  <div className="flex justify-between text-xs text-[#666] mt-2 font-medium">
                    <span>$5,000</span>
                    <span>$20,000</span>
                  </div>
                </div>

                {/* Rental Commission Slider */}
                <div>
                  <label className="block text-[#353f4f] font-semibold text-base mb-3 font-Jakarta">
                    Average Commission - Leases:{' '}
                    <span className="text-[#663ae9]">
                      ${state.tenantCommission.toLocaleString()}
                    </span>
                  </label>
                  <p className="text-sm text-[#666] mb-3 leading-relaxed font-Jakarta">
                    Average commission earned per successful lease transaction.
                    Typically ranges from $500 to $2,500 per lease.
                  </p>
                  <input
                    type="range"
                    min="500"
                    max="2500"
                    step="50"
                    value={state.tenantCommission}
                    onChange={e =>
                      updateState('tenantCommission', parseInt(e.target.value))
                    }
                    className="w-full h-2 bg-[#e0e0e0] rounded-lg appearance-none cursor-pointer roi-slider"
                    style={{
                      background: (() => {
                        const percentage =
                          ((state.tenantCommission - 500) / 2000) * 100;
                        const opacity = 0.3 + (percentage / 100) * 0.7; // Range from 0.3 to 1.0
                        return `linear-gradient(to right, rgba(102, 58, 233, ${opacity}) 0%, rgba(102, 58, 233, ${opacity}) ${percentage}%, #e0e0e0 ${percentage}%, #e0e0e0 100%)`;
                      })()
                    }}
                  />
                  <div className="flex justify-between text-xs text-[#666] mt-2 font-medium">
                    <span>$500</span>
                    <span>$2,500</span>
                  </div>
                </div>

                {/* FSBO Conversion Rate Slider - Hidden for Essential plan */}
                {currentPlan.id !== 'essential' && (
                  <div>
                    <label className="block text-[#353f4f] font-semibold text-base mb-3 font-Jakarta">
                      FSBO to Full Service:{' '}
                      <span className="text-[#663ae9]">
                        {state.fsboConversionRate}%
                      </span>
                    </label>
                    <p className="text-sm text-[#666] mb-3 leading-relaxed font-Jakarta">
                      The percentage of FSBO (For Sale By Owner) listings that
                      convert to full-service listings. Industry average is
                      typically between 6% and 9%.
                    </p>
                    <input
                      type="range"
                      min="6"
                      max="9"
                      step="0.5"
                      value={state.fsboConversionRate}
                      onChange={e =>
                        updateState(
                          'fsboConversionRate',
                          parseFloat(e.target.value)
                        )
                      }
                      className="w-full h-2 bg-[#e0e0e0] rounded-lg appearance-none cursor-pointer roi-slider"
                      style={{
                        background: (() => {
                          const percentage =
                            ((state.fsboConversionRate - 6) / 3) * 100;
                          const opacity = 0.3 + (percentage / 100) * 0.7; // Range from 0.3 to 1.0
                          return `linear-gradient(to right, rgba(102, 58, 233, ${opacity}) 0%, rgba(102, 58, 233, ${opacity}) ${percentage}%, #e0e0e0 ${percentage}%, #e0e0e0 100%)`;
                        })()
                      }}
                    />
                    <div className="flex justify-between text-xs text-[#666] mt-2 font-medium">
                      <span>6%</span>
                      <span>9%</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Card - Results Display */}
            <div
              className="bg-white rounded-2xl shadow-lg p-8 lg:p-10 relative z-0 border-t-4 border-[#663ae9]"
              style={{
                position: 'sticky',
                top: '2rem',
                alignSelf: 'flex-start'
              }}
            >
              {/* Main Savings Display - ROI Progress Bar */}
              <div className="flex flex-col mb-8">
                {/* Revenue Display */}
                <div className="text-center mb-6">
                  <div className="text-3xl lg:text-4xl font-bold text-[#353f4f] mb-1 font-Jakarta leading-tight">
                    {formatCurrency(minRevenue)} to {formatCurrency(maxRevenue)}
                  </div>
                  <div className="text-sm text-[#666] font-medium font-Jakarta">
                    Projected annual commissions
                  </div>
                </div>

                {/* ROI Progress Bar Row */}
                <div className="flex items-center gap-3 w-full">
                  {/* ROI Label */}
                  <div className="text-lg font-bold text-[#663ae9] font-Jakarta whitespace-nowrap">
                    ROI
                  </div>

                  {/* Progress Bar Container */}
                  <div className="flex-1 h-10 bg-[#e8e0f5] relative">
                    {/* Filled progress bar with multiplier at the end */}
                    <div
                      className="h-full bg-[#663ae9] transition-all duration-500 ease-out relative flex items-center justify-end pr-2"
                      style={{ width: `${roiPercentage}%` }}
                    >
                      {/* ROI Multiplier at the end of the bar */}
                      <span className="text-white font-bold text-base font-Jakarta whitespace-nowrap">
                        {avgROI.toFixed(1)}x
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Key Facts - Accordion */}
              <div className="mb-8 bg-[#f5f6f8] rounded-xl relative z-0">
                <div
                  className="cursor-pointer p-4 flex items-center justify-between"
                  onClick={() => {
                    setIsKeyFactsOpen(!isKeyFactsOpen);
                    if (!isKeyFactsOpen) {
                      setIsRevenueBreakdownOpen(false);
                      setIsLeadsBreakdownOpen(false);
                    }
                  }}
                >
                  <h4 className="text-base font-semibold text-[#353f4f] font-Jakarta">
                    Key Facts:
                  </h4>
                  <span className="text-xl text-[#353f4f]">
                    {isKeyFactsOpen ? '−' : '+'}
                  </span>
                </div>
                {isKeyFactsOpen && (
                  <div className="px-4 pb-6 space-y-3 relative z-0">
                    <div className="flex items-start gap-3">
                      <svg
                        className="w-5 h-5 text-[#663ae9] flex-shrink-0 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2.5}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <p className="text-sm text-[#353f4f] font-Jakarta">
                        Our leads are actual showing requests made on realtor.ca
                        and are actively waiting for an agent to contact them
                        ASAP
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <svg
                        className="w-5 h-5 text-[#663ae9] flex-shrink-0 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2.5}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <p className="text-sm text-[#353f4f] font-Jakarta">
                        BrokerMax leads respond 6X more than other lead delivery
                        platforms
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <svg
                        className="w-5 h-5 text-[#663ae9] flex-shrink-0 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2.5}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <p className="text-sm text-[#353f4f] font-Jakarta">
                        Each plan includes a dedicated account manager from a
                        real estate marketing expert
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Leads Breakdown - Pie Chart (Accordion) */}
              <div className="mb-8 bg-[#f5f6f8] rounded-xl relative z-0">
                <div
                  className="cursor-pointer p-4 flex items-center justify-between"
                  onClick={() => {
                    setIsLeadsBreakdownOpen(!isLeadsBreakdownOpen);
                    if (!isLeadsBreakdownOpen) {
                      setIsRevenueBreakdownOpen(false);
                      setIsKeyFactsOpen(false);
                    }
                  }}
                >
                  <h4 className="text-base font-semibold text-[#353f4f] font-Jakarta">
                    Leads Breakdown:
                  </h4>
                  <span className="text-xl text-[#353f4f]">
                    {isLeadsBreakdownOpen ? '−' : '+'}
                  </span>
                </div>
                {isLeadsBreakdownOpen && (
                  <div className="px-4 pb-6 space-y-4 relative z-0">
                    {/* Pie Chart - Full Width */}
                    <div className="w-full flex justify-center">
                      <svg
                        width="300"
                        height="300"
                        viewBox="0 0 200 200"
                        className="w-full max-w-[300px]"
                      >
                        {leadsPieChartData.map((segment, index) => (
                          <g key={index}>
                            <path
                              d={segment.path}
                              fill={segment.color}
                              stroke="white"
                              strokeWidth="2"
                            />
                            {/* Label inside pie chart */}
                            {segment.percentage > 5 && (
                              <text
                                x={segment.labelX}
                                y={segment.labelY}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                className="font-bold fill-white"
                                style={{ fontSize: '10px', fontWeight: 'bold' }}
                              >
                                {segment.chartLabel}
                              </text>
                            )}
                          </g>
                        ))}
                      </svg>
                    </div>
                    {/* Legend - Below Chart */}
                    <div className="space-y-2">
                      {leadsPieChartData.map((segment, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 text-sm font-Jakarta"
                        >
                          <div
                            className="w-4 h-4 rounded flex-shrink-0"
                            style={{ backgroundColor: segment.color }}
                          />
                          <span className="font-semibold text-[#353f4f]">
                            {segment.chartLabel}:
                          </span>{' '}
                          <span className="font-bold text-[#353f4f]">
                            {segment.min.toLocaleString()} to{' '}
                            {segment.max.toLocaleString()}
                          </span>{' '}
                          <span className="text-[#666]">per year</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Revenue Breakdown - Pie Chart (Accordion) */}
              <div className="mb-8 bg-[#f5f6f8] rounded-xl relative z-0">
                <div
                  className="cursor-pointer p-4 flex items-center justify-between"
                  onClick={() => {
                    setIsRevenueBreakdownOpen(!isRevenueBreakdownOpen);
                    if (!isRevenueBreakdownOpen) {
                      setIsLeadsBreakdownOpen(false);
                      setIsKeyFactsOpen(false);
                    }
                  }}
                >
                  <h4 className="text-base font-semibold text-[#353f4f] font-Jakarta">
                    Revenue Breakdown:
                  </h4>
                  <span className="text-xl text-[#353f4f]">
                    {isRevenueBreakdownOpen ? '−' : '+'}
                  </span>
                </div>
                {isRevenueBreakdownOpen && (
                  <div className="px-4 pb-6 space-y-4 relative z-0">
                    {/* Pie Chart - Full Width */}
                    <div className="w-full flex justify-center">
                      <svg
                        width="300"
                        height="300"
                        viewBox="0 0 200 200"
                        className="w-full max-w-[300px]"
                      >
                        {pieChartData.map((segment, index) => (
                          <g key={index}>
                            <path
                              d={segment.path}
                              fill={segment.color}
                              stroke="white"
                              strokeWidth="2"
                            />
                            {/* Label inside pie chart */}
                            {segment.percentage > 5 && (
                              <text
                                x={segment.labelX}
                                y={segment.labelY}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                className="font-bold fill-white"
                                style={{ fontSize: '10px', fontWeight: 'bold' }}
                              >
                                {segment.chartLabel}
                              </text>
                            )}
                          </g>
                        ))}
                      </svg>
                    </div>
                    {/* Legend - Below Chart */}
                    <div className="space-y-2">
                      {pieChartData.map((segment, index) => {
                        const dealsText = segment.label.includes('Listing')
                          ? `${segment.closings.split(' to ')[0]}-${segment.closings.split(' to ')[1]} listings`
                          : `${segment.closings.split(' to ')[0]}-${segment.closings.split(' to ')[1]} deals`;

                        return (
                          <div
                            key={index}
                            className="flex items-center gap-2 text-sm font-Jakarta"
                          >
                            <div
                              className="w-4 h-4 rounded flex-shrink-0"
                              style={{ backgroundColor: segment.color }}
                            />
                            <span className="font-semibold text-[#353f4f]">
                              {segment.chartLabel}:
                            </span>{' '}
                            <span className="font-bold text-[#353f4f]">
                              {formatCurrencyRange(segment.min, segment.max)}
                            </span>{' '}
                            <span className="text-[#666]">({dealsText})</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* "So, what's next?" Section */}
              <div className="border-t border-[#e0e0e0] pt-8">
                <h3 className="text-xl font-bold text-[#353f4f] mb-4 font-Jakarta">
                  So, what&apos;s next?
                </h3>
                <p className="text-[#666] text-base leading-relaxed mb-6 font-Jakarta">
                  {currentPlan.id === 'essential'
                    ? 'Start free trial and save 50% off your first month'
                    : 'Schedule a call with a BrokerMax expert.'}
                </p>
                {currentPlan.id === 'essential' ? (
                  <Link
                    to="/trial"
                    className="btn r-04 !rounded-[4px] btn--blue-400 hover--blue-500 w-full"
                  >
                    Start Trial Now →
                  </Link>
                ) : (
                  <Link
                    to="/schedule-call"
                    className="btn r-04 !rounded-[4px] btn--blue-400 hover--blue-500 w-full"
                  >
                    Schedule Call →
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
